import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { num } from '../../format';
import { LocationPicker } from '../locations/LocationPicker';
import { useNavigation } from '../layout/navigation';
import { locationReport } from './api';

export function LocationReportPanel() {
  const { t } = useTranslation();
  const { open } = useNavigation();
  const [search, setSearch] = useSearchParams();
  const locationId = search.get('location');
  const [includeDescendants, setIncludeDescendants] = useState(search.get('descendants') === '1');
  const [filter, setFilter] = useState('');
  const report = useQuery({
    queryKey: ['stock', 'location-report', locationId, includeDescendants],
    queryFn: () => locationReport(locationId!, includeDescendants),
    enabled: Boolean(locationId),
  });

  const update = (location: string | null, descendants = includeDescendants) => {
    const next = new URLSearchParams(search);
    if (location) next.set('location', location); else next.delete('location');
    if (descendants) next.set('descendants', '1'); else next.delete('descendants');
    next.set('tab', 'locations');
    setIncludeDescendants(descendants);
    setSearch(next, { replace: true });
  };

  const lines = useMemo(() => {
    const query = filter.trim().toLocaleLowerCase();
    if (!query) return report.data?.lines ?? [];
    return (report.data?.lines ?? []).filter((line) => [line.component_code, line.component_name, line.location_name]
      .some((value) => value.toLocaleLowerCase().includes(query)));
  }, [filter, report.data?.lines]);

  return (
    <section className="location-report">
      <header>
        <div>
          <h2>{t('stock.locationReport.title')}</h2>
          <p>{t('stock.locationReport.hint')}</p>
        </div>
      </header>
      <div className="location-report__controls">
        <LocationPicker
          label={t('stock.locationReport.location')}
          value={locationId}
          onChange={(value) => update(value)}
          allowOutside={false}
          scanTarget
        />
        <label className="check">
          <input
            type="checkbox"
            checked={includeDescendants}
            disabled={!locationId}
            onChange={(event) => update(locationId, event.target.checked)}
          />
          {t('stock.locationReport.descendants')}
        </label>
      </div>

      {!locationId && <p className="panel__hint">{t('stock.locationReport.pick')}</p>}
      {locationId && report.isLoading && <p className="panel__hint">{t('common.loading')}</p>}
      {report.data && (
        <>
          <div className="location-report__summary">
            <strong>{t('stock.locationReport.positions', { n: lines.length })}</strong>
            <label><span className="sr-only">{t('stock.locationReport.search')}</span><input type="search" value={filter} onChange={(event) => setFilter(event.target.value)} placeholder={t('stock.locationReport.search')} /></label>
          </div>
          {lines.length === 0 && <p className="panel__hint">{t(filter ? 'stock.locationReport.noneMatch' : 'stock.locationReport.empty')}</p>}
          {lines.length > 0 && <div className="location-report__table-wrap"><table className="location-report__table"><thead><tr><th>{t('stock.locationReport.component')}</th><th>{t('stock.locationReport.location')}</th><th>{t('stock.locationReport.balance')}</th></tr></thead><tbody>{lines.map((line) => <tr key={`${line.location_id}:${line.component_id}`}><td><button type="button" className="link" onClick={() => open('component', line.component_id)}>{line.component_code} · {line.component_name}</button>{line.track_serials && <small>{t('stock.locationReport.serials')}</small>}</td><td><button type="button" className="link" onClick={() => open('location', line.location_id)}>{line.location_name}</button></td><td><strong>{num(line.qty)}</strong></td></tr>)}</tbody></table></div>}
        </>
      )}
    </section>
  );
}
